import { assertUnreachable, isDefined, isNonEmpty, isNotDefined } from '@sgme/fp';
import { Box, HStack, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { clsx } from 'clsx';
import { type RefObject, useEffect, useRef } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { optionApi } from '@/api/option/index.ts';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { FormattedPremium } from '@/App/tiles/Option/utils/FormattedPremium.tsx';
import { getAssociatedPremiumCategory, getPremiumFromQuote } from '@/App/tiles/Option/utils/premiumUtils.ts';
import { ErrorRates } from '@/App/tiles/shared/ErrorRates.tsx';
import { ForbiddenExecutionTooltip } from '@/App/tiles/shared/ForbiddenExecutionTooltip.tsx';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { getUserEmail } from '@/sgwt/sgwtConnect.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import {
  getQuote,
  getRfsId,
  selectOptionQuoteLegs,
  selectRfsStatus,
} from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption, LegId, PremiumType } from '@/store/option/state.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';
import { getIsStreamingConnected } from '@/store/streaming/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import type { Way } from '@/types/common.ts';
import type { IntlMessageId } from '@/types/intl';
import { getRateComponentStyling } from '@/utils/rateComponentStyling.ts';

type BidAskProps = {
  tileId: string;
};

export function BidAsk({ tileId }: BidAskProps) {
  const rfsStatus = useOptionSelector((option) => selectRfsStatus(option));
  const quote = useOptionSelector((option) => getQuote(option));

  switch (rfsStatus) {
    case 'NOT_PRICEABLE':
      return <NotPriceable />;
    case 'PRICEABLE':
      if (isDefined(quote)) {
        return <Streaming tileId={tileId} />;
      }
      return <Priceable />;
    case 'AWAITING_INDICATIVE':
      return <Awaiting forceTradeable={false} />;
    case 'AWAITING_EXECUTABLE':
      return <Awaiting forceTradeable={true} />;
    case 'STREAMING_INDICATIVE':
      return <Streaming tileId={tileId} />;
    case 'STREAMING_EXECUTABLE':
      return <Streaming tileId={tileId} executable={true} />;
    case 'ERROR':
      return <StreamingError />;
    case 'BYPASSABLE_ERROR':
      return <StreamingError />;
    default:
      assertUnreachable(rfsStatus, `Unknown price status: ${rfsStatus}`);
  }
}

function Streaming({ tileId, executable = false }: { tileId: string; executable?: boolean }) {
  const streamStatus = useOptionSelector((option) => option.properties.rfs.status);

  const expiredPrice = streamStatus === 'EXPIRED';
  return (
    <HStack
      fullWidth
      className="position-relative"
      data-e2e="rates"
      data-e2e-status={expiredPrice ? 'expired' : 'pricing'}
    >
      <HStack horizontalAlignment="center" className="position-absolute top-0 start-0 end-0">
        <AtmVolatilityOrMessage />
      </HStack>
      <Rate tileId={tileId} way="Bid" executable={executable} />
      <Rate tileId={tileId} way="Ask" executable={executable} />

      <div className="d-flex flex-center position-absolute bottom-0 start-0 end-0">
        <MidVolatility />
      </div>
    </HStack>
  );
}

function AtmVolatilityOrMessage() {
  const { expiredPrice, salesTiering, midVolatility } = useOptionSelector((option) => {
    const tileId = option.properties.tileId;
    const legId: LegId = `${tileId}/0`;
    const legData = selectOptionQuoteLegs(option, legId);
    const midVolatility = legData?.midVolatility;
    const streamStatus = option.properties.rfs.status;
    const expiredPrice = streamStatus === 'EXPIRED';
    const quote = getQuote(option);
    const salesTiering = quote?.tiering === 'STREAMDEFAULT';
    return {
      expiredPrice,
      salesTiering,
      midVolatility,
    };
  });

  if (expiredPrice) {
    return (
      <span className="badge rounded-pill badge-secondary fs-12 my-1 py-0" data-e2e="expired-price">
        <FormattedMessage id="streaming.expired.price" />
      </span>
    );
  }

  if (salesTiering) {
    return (
      <span className="badge rounded-pill badge-warning fs-12 my-1 py-0" data-e2e="sales-tier">
        Sales tiering
      </span>
    );
  }

  return (
    <div className="px-1 text-black bg-light monospace-numbers p-1" data-e2e="atm-volatility">
      A:{' '}
      {isDefined(midVolatility?.atmVolatilityMid) ? (
        <FormattedNumber value={midVolatility?.atmVolatilityMid} minimumFractionDigits={3} />
      ) : (
        '-'
      )}
    </div>
  );
}

function MidVolatility() {
  const { midVolatility } = useOptionSelector((option) => {
    const tileId = option.properties.tileId;
    const legId: LegId = `${tileId}/0`;
    const legData = selectOptionQuoteLegs(option, legId);
    return { midVolatility: legData?.midVolatility };
  });

  if (isNotDefined(midVolatility?.smiledVolatilityMid)) {
    return null;
  }

  return (
    <div className="p-1 fs-6 text-black bg-light monospace-numbers" data-e2e="mid-volatility">
      M: <FormattedNumber value={midVolatility.smiledVolatilityMid} minimumFractionDigits={3} />
    </div>
  );
}

interface RateProps {
  tileId: string;
  way: Way;
  executable: boolean;
}

function Rate({ tileId, way, executable }: RateProps) {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const hasTradePermission = useCanTrade();
  const [outer, inner] = useScaleDown<HTMLDivElement, HTMLDivElement>();

  const legId: LegId = `${tileId}/0`;

  const oneWayQuotation = useAppSelector((state) => state.userPreferences.optionOneWayQuotation);
  const showColoredPrices = useAppSelector((state) => state.userPreferences.showColoredPrices);
  const {
    premiumAmount,
    premiumMessage,
    associatedPremiumAmount,
    premiumType,
    associatedPremiumType,
    isExpired,
    isClientWay,
    strategyPanelVisible,
    realWay,
    isTcReady,
  } = useOptionSelector((option) => {
    const premiumType = option.properties.premiumType;

    const isTcReady = option.tradeCapture.status === 'CHECKED';
    const premium = getPremiumFromQuote(premiumType, option, legId);
    const associatedPremiumType = {
      premiumCurrency: premiumType.premiumCurrency,
      premiumCategory: getAssociatedPremiumCategory(premiumType.premiumCategory),
    };
    const associatedPremium = getPremiumFromQuote(associatedPremiumType, option, legId);
    const strategyPanelVisible = option.properties.uiState.strategyPanelMode !== 'hidden';
    const majorPremiumIsMissing = isPotentialMajorValueUnavailable(option, legId, premiumType);

    const realWay = premium.isReversed ? reverseWay(way) : way;
    const isClientWay = realWay === 'Ask';

    const premiumIsNegative = way === 'Bid' ? premium.bidIsNegative : premium.askIsNegative;

    const messageWay: Way = premiumIsNegative ? reverseWay(realWay) : realWay;
    const premiumMessage = messageWay === 'Bid' ? ('option.way.receive' as const) : ('option.way.pay' as const);

    return {
      premiumAmount: majorPremiumIsMissing
        ? way === 'Bid'
          ? associatedPremium.bid
          : associatedPremium.ask
        : way === 'Bid'
          ? premium.bid
          : premium.ask,
      premiumMessage,
      premiumType: majorPremiumIsMissing ? associatedPremiumType : premiumType,
      associatedPremiumAmount: majorPremiumIsMissing
        ? undefined
        : way === 'Bid'
          ? associatedPremium.bid
          : associatedPremium.ask,
      associatedPremiumType: majorPremiumIsMissing ? undefined : associatedPremiumType,
      isExpired: option.properties.rfs.status === 'EXPIRED',
      isClientWay,
      strategyPanelVisible,
      realWay,
      isTcReady,
    };
  });

  const showOneWay = oneWayQuotation && strategyPanelVisible;
  const wrongWay = !isClientWay && showOneWay;
  const canTrade = hasTradePermission && !wrongWay;

  function handleExecution() {
    sgwtWebAnalytics?.trackEvent('Option', 'Execution', realWay);
    dispatch(executeOptionThunk({ tileId, way: realWay }));
  }

  const isReadyExecutable = executable && isTcReady;
  return (
    <Box ref={outer} className={clsx('col-6', { 'border-end': way === 'Bid' })}>
      <ForbiddenExecutionTooltip placement={way === 'Bid' ? 'left' : 'right'}>
        <div
          className={clsx(
            'px-2 py-1',
            {
              expired: isExpired,
              bid: way === 'Bid',
              ask: way === 'Ask',
              'colored-prices': showColoredPrices,
            },
            ...getRateComponentStyling({ executable: isReadyExecutable && !isExpired, cantTrade: !canTrade }),
            way === 'Bid' && strategyPanelVisible && 'text-secondary fw-bold',
          )}
          onDoubleClick={canTrade && isReadyExecutable ? () => handleExecution() : undefined}
          data-e2e={`premium-${way.toLowerCase()}`}
          data-e2e-rate-type={isReadyExecutable ? 'executable' : 'indicative'}
          data-e2e-can-trade={canTrade}
        >
          <PremiumWayMessage data-e2e={`premium-${way.toLowerCase()}-label`} labelId={premiumMessage} />
          <div className="fs-12 lh-1" data-e2e={`associated-${way.toLowerCase()}-price`}>
            {isDefined(associatedPremiumAmount) &&
              isDefined(associatedPremiumType) &&
              associatedPremiumAmount !== 0 && (
                <FormattedPremium premiumAmount={associatedPremiumAmount} premiumType={associatedPremiumType} />
              )}
          </div>
          <div ref={inner} data-e2e={`premium-${way.toLowerCase()}-value`} className="align-self-center p-2">
            <FormattedPremium className="h3" premiumAmount={premiumAmount} premiumType={premiumType} />
          </div>
          <OptionWayMessage data-e2e={`premium${way}-way-label`} way={realWay} />
        </div>
      </ForbiddenExecutionTooltip>
    </Box>
  );
}

function isPotentialMajorValueUnavailable(option: FxOption, legId: LegId, premiumType: PremiumType): boolean {
  const quote = getQuote(option);

  switch (premiumType.premiumCategory) {
    case 'AMOUNT': {
      if (isNotDefined(quote)) {
        return true;
      }

      const premium = premiumType.premiumCurrency === 1 ? quote.ccy1 : quote.ccy2;
      return hasNoDisplayedValue(premium.bidAmount, premium.askAmount);
    }
    case 'PERCENT': {
      if (isNotDefined(quote)) {
        return true;
      }

      const premium = premiumType.premiumCurrency === 1 ? quote.ccy1 : quote.ccy2;
      return hasNoDisplayedValue(premium.bidPercent, premium.askPercent);
    }
    case 'PPS': {
      if (isNotDefined(quote)) {
        return true;
      }

      const premium = premiumType.premiumCurrency === 1 ? quote.ccy1 : quote.ccy2;
      return hasNoDisplayedValue(premium.bidPps, premium.askPps);
    }
    case 'VOLATILITY': {
      const volatility = selectOptionQuoteLegs(option, legId)?.marketData?.volatility;
      return isNotDefined(volatility) || hasNoDisplayedValue(volatility.bid, volatility.ask);
    }
    default:
      return assertUnreachable(premiumType.premiumCategory, `Unknown premium category: ${premiumType.premiumCategory}`);
  }
}

function hasNoDisplayedValue(bid: number | null | undefined, ask: number | null | undefined): boolean {
  return (bid === 0 && ask === 0) || (isNotDefined(bid) && isNotDefined(ask));
}

function reverseWay(way: Way) {
  return way === 'Bid' ? 'Ask' : 'Bid';
}

function PremiumWayMessage({ labelId, 'data-e2e': dataE2e }: { labelId: IntlMessageId; 'data-e2e': string }) {
  const {
    currentUser: { userType },
  } = useUserContext();
  const isInternalSales = userType === 'Internal Sales';

  return (
    <div className="fs-12 lh-1" data-e2e={dataE2e}>
      <FormattedMessage id={labelId} values={{ isInternalSales }} />
    </div>
  );
}

function OptionWayMessage({ way, 'data-e2e': dataE2e }: { way: Way; 'data-e2e': string }) {
  const {
    currentUser: { userType },
  } = useUserContext();
  const isInternalSales = userType === 'Internal Sales';
  const isClientWay = way === 'Ask';

  const { strategyPanelVisible, productName } = useOptionSelector((option) => {
    return {
      strategyPanelVisible: option.properties.uiState.strategyPanelMode !== 'hidden',
      productName: option.type,
    };
  });

  if (strategyPanelVisible && isClientWay) {
    return (
      <div className="fs-12" data-e2e={dataE2e}>
        <FormattedMessage id={'option.fields.your.way'} values={{ isInternalSales }} />
      </div>
    );
  }

  if (strategyPanelVisible && !isClientWay) {
    return <div data-e2e={dataE2e}>&nbsp;</div>;
  }

  return (
    <div className="fs-12" data-e2e={dataE2e}>
      {productName === 'RiskReversal' ? (
        <FormattedMessage
          id={way === 'Bid' ? 'option.way.RiskReversal.sell' : 'option.way.RiskReversal.buy'}
          values={{ isInternalSales }}
        />
      ) : (
        <FormattedMessage id={way === 'Bid' ? 'option.way.sell' : 'option.way.buy'} values={{ isInternalSales }} />
      )}
    </div>
  );
}

function NotPriceable() {
  return (
    <HStack data-e2e="rates" fullWidth>
      <div className="w-50 border-end text-center bg-lvl2" style={{ height: 100 }}>
        <VStack
          horizontalAlignment="center"
          verticalAlignment="center"
          fullWidth
          fullHeight
          className="fw-semibold text-secondary bg-lvl2"
          data-e2e="fill-in-bid-price"
        >
          <FormattedMessage id="streaming.fill.in.bid.price" values={{ br: <br /> }} />
        </VStack>
      </div>
      <div className="w-50 text-center bg-lvl2" style={{ height: 100 }}>
        <VStack
          horizontalAlignment="center"
          verticalAlignment="center"
          fullWidth
          fullHeight
          className="fw-semibold text-secondary bg-lvl2"
          data-e2e="fill-in-ask-price"
        >
          <FormattedMessage id="streaming.fill.in.ask.price" values={{ br: <br /> }} />
        </VStack>
      </div>
    </HStack>
  );
}

function Priceable() {
  return (
    <HStack data-e2e="rates" fullWidth className="">
      <div className="w-50 border-end text-center bg-lvl2" style={{ height: 100 }}>
        <VStack
          horizontalAlignment="center"
          verticalAlignment="center"
          fullWidth
          fullHeight
          className="fw-semibold text-secondary bg-lvl2"
          data-e2e="fill-in-bid-price"
        >
          <FormattedMessage id="streaming.reprice.bid.price" values={{ br: <br /> }} />
        </VStack>
      </div>
      <div className="w-50 text-center bg-lvl2" style={{ height: 100 }}>
        <VStack
          horizontalAlignment="center"
          verticalAlignment="center"
          fullWidth
          fullHeight
          className="fw-semibold text-secondary bg-lvl2"
          data-e2e="fill-in-ask-price"
        >
          <FormattedMessage id="streaming.reprice.ask.price" values={{ br: <br /> }} />
        </VStack>
      </div>
    </HStack>
  );
}

function Awaiting({ forceTradeable }: { forceTradeable: boolean }) {
  const message = forceTradeable ? 'streaming.awaiting.trader' : 'streaming.awaiting';

  return (
    <HStack data-e2e="rates" fullWidth className="">
      <div className="w-50 border-end text-center bg-lvl2" style={{ height: 100 }}>
        <VStack
          horizontalAlignment="center"
          verticalAlignment="center"
          fullWidth
          fullHeight
          className="fw-semibold text-secondary bg-lvl2"
          data-e2e="fill-in-bid-price"
        >
          <FormattedMessage id={message} />
        </VStack>
      </div>
      <div className="w-50 text-center bg-lvl2" style={{ height: 100 }}>
        <VStack
          horizontalAlignment="center"
          verticalAlignment="center"
          fullWidth
          fullHeight
          className="fw-semibold text-secondary bg-lvl2"
          data-e2e="fill-in-ask-price"
        >
          <FormattedMessage id={message} />
        </VStack>
      </div>
    </HStack>
  );
}

function StreamingError() {
  const { errorCode, errorMessage, multipassErrorCode, rfsId, predealChecks } = useOptionSelector((option) => {
    const rfsId = getRfsId(option);
    if (option.properties.rfs.status !== 'ERROR') {
      return {
        errorCode: undefined,
        errorMessage: undefined,
        multipassErrorCode: undefined,
        rfsId: rfsId,
        predealChecks: [],
      };
    }

    return {
      errorCode: option.properties.rfs.error.errorCode,
      errorMessage: option.properties.rfs.error.errorMessage,
      multipassErrorCode: option.properties.rfs.error.multipassErrorCode,
      rfsId,
      predealChecks: getQuote(option)?.predealChecks ?? [],
    };
  });

  function computeErrorTranslationKey(errorCode: string, errorMessage: string): string {
    const newErrorCode = errorMessage.substring(0, errorMessage.indexOf('|'));
    if (isNonEmpty(newErrorCode)) {
      return `${newErrorCode}_${errorCode}`;
    }
    return errorCode;
  }

  const errorKey =
    isDefined(errorMessage) && isDefined(errorCode) ? computeErrorTranslationKey(errorCode, errorMessage) : errorCode;

  const multipass = isDefined(errorCode) ? { code: multipassErrorCode, sessionId: rfsId, predealChecks } : undefined;

  return <ErrorRates code={errorKey} message={errorMessage} multipass={multipass} />;
}

function useCanTrade(): boolean {
  const userContextValue = useUserContext();
  const userCanTrade = isDefined(userContextValue) ? checkIfUserCanTrade(userContextValue) : false;

  const isStreamingConnected = useAppSelector(getIsStreamingConnected);

  return userCanTrade && isStreamingConnected;
}

export function useScaleDown<Outer extends HTMLElement = HTMLElement, Inner extends HTMLElement = HTMLElement>(): [
  RefObject<Outer | null>,
  RefObject<Inner | null>,
] {
  const outerRef = useRef<Outer>(null);
  const innerRef = useRef<Inner>(null);

  useEffect(() => {
    if (outerRef.current && innerRef.current) {
      const outer = outerRef.current.clientWidth;
      const inner = innerRef.current.clientWidth;

      if (inner > outer) {
        innerRef.current.style.transform = `scale(${outer / inner})`;
      } else {
        innerRef.current.style.transform = '';
      }
    }
  });

  return [outerRef, innerRef];
}

const executeOptionThunk = createAppAsyncThunk<void, { tileId: string; way: 'Bid' | 'Ask' }>(
  'option/executeOptionRfs',
  async (payload, { getState, dispatch }) => {
    const { tileId, way } = payload;

    const state = getState();

    const option = state.option[tileId];
    if (isNotDefined(option)) {
      return;
    }

    const preferedEmails = state.userPreferences.splitNotifications
      ? state.userPreferences.splitNotificationsEmailsOptions
      : state.userPreferences.emails;

    const clientId = selectTileClientId(state, tileId);
    const notificationEmails = preferedEmails.length > 0 ? preferedEmails.join(';') : getUserEmail();
    const rfsId = getRfsId(option);
    const quote = getQuote(option);
    const quoteId = quote?.quoteId;

    if (
      isNotDefined(quoteId) ||
      isNotDefined(rfsId) ||
      isNotDefined(clientId) ||
      isNotDefined(notificationEmails) ||
      isNotDefined(quote)
    ) {
      return;
    }

    dispatch(
      optionApi.endpoints.executeOptionRfs.initiate(
        {
          notificationEmails,
          tileId,
          quoteId,
          accountId: String(clientId),
          rfsId: rfsId,
          way,
          lastQuoteReceivedByFrontTimestamp: quote.frontTimestamp,
          lastQuoteFromBackTimestamp: quote.backTimestamp,
        },
        { fixedCacheKey: rfsId },
      ),
    );
  },
);
